import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { getSelectedId, ORGANISATIONS, TENANTS } from '../reducers';
import { Questions } from './questions.actions';
import { CreateQuestionAnswerDto, QuestionsService } from '@careapp-group/ng-sdk-v2-new';
import { Router } from '@angular/router';
import { CAAction } from '../actions';

@Injectable()
export class QuestionsEffects {
  // :TODO: (Trent Bowden): CA-1297 determine correct type, instead of 'any'
  createQuestion$ = createEffect(() =>
    this.actions.pipe(
      ofType(Questions.REQUEST_CREATE),
      withLatestFrom(
        this.store.select(getSelectedId(ORGANISATIONS)),
        this.store.select(getSelectedId(TENANTS))
      ),
      switchMap(([action, organisationId, tenantId]) =>
        this.questionsService
          .createQuestion({
            createQuestionDto: { ...(<any>action).payload, organisationId },
          })
          .pipe(
            tap(() =>
              this.router.navigate(['/', tenantId, 'organisations', organisationId, 'feed'])
            ),
            map((result) => Questions.ResponseSuccessCreateAction(result)),
            catchError((error) => of(Questions.ResponseFailCreateAction(error)))
          )
      )
    )
  );

  listQuestions$ = createEffect(() =>
    this.actions.pipe(
      ofType(Questions.requestQuestions),
      withLatestFrom(
        this.store.select(getSelectedId(ORGANISATIONS)),
        this.store.select(getSelectedId(TENANTS))
      ),
      switchMap(([action, organisationId, tenantId]) =>
        this.questionsService
          .getQuestions({
            organisationId,
            tenantId,
            includeForAllRoles: action.includeForAllRoles ? true : false,
            includeExpired: action.includeExpired ? true : undefined,
          })
          .pipe(
            map((result) => Questions.ResponseSuccessAllAction(result)),
            catchError((error) => of(Questions.ResponseFailAllAction(error)))
          )
      )
    )
  );

  answerQuestion$ = createEffect(() =>
    this.actions.pipe(
      ofType(Questions.REQUEST_ANSWER_QUESTION),
      switchMap((action) => {
        const typedPayload = (<any>action).payload as {
          questionId: string;
          answer: CreateQuestionAnswerDto;
        };
        return this.questionsService
          .answerQuestion({
            id: typedPayload.questionId,
            createQuestionAnswerDto: typedPayload.answer,
          })
          .pipe(
            map((result) => Questions.ResponseSuccessAnswerQuestion(result)),
            catchError((error) => of(Questions.ResponseFailAnswerQuestion(error)))
          );
      })
    )
  );

  deleteQuestions$ = createEffect(() =>
    this.actions.pipe(
      ofType<CAAction>(Questions.REQUEST_DESTROY_BY_ID),
      switchMap((action) => {
        return this.questionsService.deleteQuestion({ id: action.payload }).pipe(
          map((result) => Questions.ResponseSuccessDestroyByIdAction({ id: action.payload })),
          catchError((error) =>
            of(Questions.ResponseFailDestroyByIdAction({ error, id: action.payload }))
          )
        );
      })
    )
  );

  constructor(
    private actions: Actions,
    private store: Store<any>,
    private questionsService: QuestionsService,
    private router: Router
  ) {}
}
